import APIController from '../api';
import { normalizeContext } from '../utils/textUtils';

// Priority when several sources answer: curated KB first, then per-room
// content, then the uploaded-document index.
const SOURCE_PRIORITY = ['backendKB', 'roomsData', 'indexSearch'];

// Once any source has produced a usable result, stop waiting for the others
// after this long (or at SOFT_TIMEOUT_MS, whichever comes first). The KB
// answers in ~15 ms; at 1500 ms the visitor mostly waited on this timer.
const GRACE_MS = 400;
// Slow sources are dropped at this point if something faster already answered.
const SOFT_TIMEOUT_MS = 4000;
// Nothing at all yet — keep waiting up to here, so a tour whose only source is
// slow (e.g. indexSearch) still gets its answer. Pending fetches are aborted.
const HARD_TIMEOUT_MS = 9000;

// Injected context cap and how many sources may be combined into it.
const MAX_CONTEXT_CHARS = 3000;
const MAX_COMBINED_SOURCES = 2;

// The old router (/route-rag-query, a gpt-4o-mini call) sat sequentially in
// front of every lookup. Off by default; when on, it only narrows the parallel
// set — there is no second fallback round either way.
const USE_ROUTER = false;

const STOPWORDS = new Set(['what', 'when', 'where', 'which', 'who', 'whom', 'whose', 'why', 'how',
  'the', 'and', 'for', 'are', 'was', 'were', 'did', 'does', 'this', 'that', 'with', 'from',
  'about', 'tell', 'can', 'you', 'have', 'has', 'there', 'their', 'they', 'her', 'his', 'she']);

/**
 * Up to `limit` chars of `text`: the leading header lines (URL / Title / Intro)
 * plus the sentences that share the most words with `query`, in their original
 * order. Text that already fits is returned unchanged.
 */
export function bestPassage(text, query, limit) {
  if (text.length <= limit) return text;
  const terms = (query.toLowerCase().match(/[\p{L}\p{N}]{3,}/gu) || []).filter(w => !STOPWORDS.has(w));
  const descAt = text.search(/\bDescription:/);
  const headEnd = descAt > 0 && descAt < limit / 2 ? descAt : 0;
  const head = text.slice(0, headEnd);
  // Sentence = up to [.!?] followed by whitespace, so "annefrank.org" or
  // "52.37" aren't split. (No lookbehind: older iOS Safari can't parse it.)
  const parts = text.slice(headEnd).split(/([.!?]+\s+)/);
  const sentences = [];
  for (let i = 0; i < parts.length; i += 2) {
    const s = (parts[i] + (parts[i + 1] || '')).trim();
    if (s) sentences.push(s);
  }
  const scored = sentences.map((s, i) => {
    const low = s.toLowerCase();
    const score = terms.reduce((n, t) => n + (low.includes(t) ? 1 : 0), 0);
    return { i, s, score };
  });
  const budget = limit - head.length;
  const picked = [];
  let used = 0;
  [...scored].sort((a, b) => b.score - a.score || a.i - b.i).forEach(x => {
    if (used + x.s.length + 1 > budget) return;
    picked.push(x);
    used += x.s.length + 1;
  });
  if (!picked.length) return text.slice(0, limit);
  return head + picked.sort((a, b) => a.i - b.i).map(x => x.s).join(' ');
}

// Content words of a question, for comparing the visitor's transcript with
// the query the model sends to search_knowledge_base.
export function queryTerms(text) {
  return new Set(((text || '').toLowerCase().match(/[\p{L}\p{N}]{3,}/gu) || []).filter(w => !STOPWORDS.has(w)));
}

/**
 * Agentic RAG: queries every configured source in parallel, takes what the
 * fast ones return, and injects up to two non-empty results (by priority)
 * capped at MAX_CONTEXT_CHARS.
 *
 * Flow:
 *   1. Build list of available sources (based on tourData config)
 *   2. Fetch all of them in parallel, each with its own AbortController
 *   3. Finish when all settled, or GRACE_MS after the first usable result,
 *      or SOFT_TIMEOUT_MS with a result, or HARD_TIMEOUT_MS regardless
 *   4. Combine up to 2 non-empty results in priority order and inject them
 *
 * Drop-in replacement for RAGManager — same constructor contract.
 */
class AgenticRAGManager {
  constructor({ getInstructions, sendSessionUpdate, createMessage }) {
    this._getInstructions = getInstructions;
    this._sendSessionUpdate = sendSessionUpdate;
    this._createMessage = createMessage;
  }

  _availableSources(tourData) {
    const available = [];
    if (tourData?.ragLinks?.length) {
      available.push({
        name: 'indexSearch',
        description: 'Uploaded tour/property documents — LLM-navigated index for factual lookups.'
      });
    }
    if (tourData?.roomsData?.startsWith('http')) {
      available.push({
        name: 'roomsData',
        description: 'Per-room content — layout, features, dimensions, furniture, equipment, exhibits, artwork, and objects in each room.'
      });
    }
    if (tourData?.kbSelect) {
      available.push({
        name: 'backendKB',
        description: 'Curated knowledge base — admin-added Q&A, pricing, specific facts about this tour.'
      });
    }
    return available;
  }

  /**
   * @param {object} [ctx] — { roomName, tourType }: where the visitor is. Sent
   *   as the rooms search tie-breaker and as steering for the KB relevance filter.
   */
  async updateContext(text, tourData, ctx = {}) {
    const result = await this.lookup(text, tourData, ctx);
    if (result.contextText) this.inject(result);
    return result;
  }

  // Context for the answer, injected into the conversation.
  inject({ contextText, rag_source, rag_ms }) {
    this._createMessage('system', `Context for your answer:\n${contextText}`);
    console.log(`[tokens] AgenticRAG inject (${rag_source}) | ${contextText.length} chars (~${Math.round(contextText.length / 4)} tokens) | ${rag_ms}ms`);
  }

  /**
   * Fetches context without injecting it — used directly to start a lookup
   * from the transcript before the model asks for it.
   * @returns {{updated, contextText?, rag_ms?, rag_source?, rag_detail?}}
   */
  async lookup(text, tourData, ctx = {}) {
    ctx = ctx || {};
    if (!text || !text.trim()) {
      console.log('🤖 AgenticRAG: skipped empty text');
      return { updated: false };
    }
    const _t0 = performance.now();

    const available = this._availableSources(tourData);
    if (!available.length) {
      console.log('🤖 AgenticRAG: no sources configured');
      return { updated: false };
    }

    let names = available.map(s => s.name);
    if (USE_ROUTER && names.length > 1) {
      try {
        const picked = await APIController.routeRagQuery({ question: text, sources: available });
        const narrowed = names.filter(n => (picked || []).includes(n));
        if (narrowed.length) names = narrowed;
        console.log(`🤖 AgenticRAG: router selected [${names.join(', ')}]`);
      } catch (err) {
        console.warn('🤖 AgenticRAG: routing failed, querying all sources', err);
      }
    }

    console.log(`🤖 AgenticRAG: fetching [${names.join(', ')}] in parallel`);
    const { results, timings, stop } = await this._fetchAll(names, text, tourData, _t0, ctx);
    const rag_ms = Math.round(performance.now() - _t0);

    const timingStr = names.map(n => `${n}=${timings[n] || 'pending'}`).join(' ');
    console.log(`🤖 AgenticRAG: timing ${timingStr} total=${rag_ms}ms`);
    // Saved with the answer (conversations.rag_detail): which source made the
    // lookup slow and why waiting stopped — avatar/scripts/rag_latency_report.py.
    const rag_detail = `${names.map(n => `${n}=${timings[n] || 'pending'}`).join(',')};stop=${stop}`;

    const hits = SOURCE_PRIORITY
      .map(name => results[name])
      .filter(r => r && r.text)
      .slice(0, MAX_COMBINED_SOURCES);

    if (!hits.length) {
      // 'error' only when no source answered cleanly (all failed / timed out)
      const anyClean = names.some(n => timings[n] && /^\d+ms$/.test(timings[n]));
      const rag_source = anyClean ? 'none' : 'error';
      console.log(`🤖 AgenticRAG: no source produced results (${rag_source}) | ${rag_ms}ms`);
      return { updated: false, rag_ms, rag_source, rag_detail };
    }

    const contextText = this._combine(hits);
    const rag_source = hits.map(h => h.source).join('+');
    return { updated: true, contextText, rag_ms, rag_source, rag_detail };
  }

  // Concatenate in priority order, truncating so the total stays under the cap.
  _combine(hits) {
    let out = '';
    for (const h of hits) {
      const sep = out ? '\n\n' : '';
      const room = MAX_CONTEXT_CHARS - out.length - sep.length;
      if (room < 200) break;
      const body = h.text.length > room ? `${h.text.slice(0, room - 1)}…` : h.text;
      out += sep + body;
    }
    return out;
  }

  /**
   * Starts every fetch at once and resolves according to the GRACE / SOFT /
   * HARD rules above. Fetches still pending at that point are aborted.
   * timings[name] is "NNNms" (answered, empty or not), "error@NNNms" or
   * "timeout@NNNms".
   */
  _fetchAll(names, text, tourData, t0, ctx) {
    return new Promise(resolve => {
      const results = {};
      const timings = {};
      const controllers = {};
      let pending = names.length;
      let firstHitAt = null;
      let done = false;
      let graceTimer = null;

      const finish = (reason) => {
        if (done) return;
        done = true;
        clearTimeout(softTimer);
        clearTimeout(hardTimer);
        clearTimeout(graceTimer);
        const now = Math.round(performance.now() - t0);
        names.forEach(n => {
          if (!timings[n]) {
            timings[n] = `timeout@${now}ms`;
            try { controllers[n].abort(); } catch (_) {}
          }
        });
        if (reason !== 'all') console.log(`🤖 AgenticRAG: stopped waiting (${reason})`);
        resolve({ results, timings, stop: reason });
      };

      const softTimer = setTimeout(() => { if (firstHitAt != null) finish('soft-timeout'); }, SOFT_TIMEOUT_MS);
      const hardTimer = setTimeout(() => finish('hard-timeout'), HARD_TIMEOUT_MS);

      names.forEach(name => {
        const ctrl = new AbortController();
        controllers[name] = ctrl;
        const started = performance.now();
        this._fetchSource(name, text, tourData, ctrl.signal, ctx)
          .then(value => {
            if (done) return;
            timings[name] = `${Math.round(performance.now() - started)}ms`;
            if (value && value.text) {
              results[name] = value;
              if (firstHitAt == null) {
                firstHitAt = performance.now();
                graceTimer = setTimeout(() => finish('grace'), GRACE_MS);
              }
            }
          })
          .catch(err => {
            if (done) return;
            timings[name] = `error@${Math.round(performance.now() - started)}ms`;
            console.warn(`🤖 AgenticRAG: ${name} error`, err);
          })
          .finally(() => {
            if (done) return;
            pending -= 1;
            if (pending === 0) finish('all');
          });
      });
    });
  }

  _fetchSource(name, text, tourData, signal, ctx) {
    switch (name) {
      case 'indexSearch': return this._fetchIndexSearch(text, tourData, signal);
      case 'roomsData':   return this._fetchRoomsData(text, tourData, signal, ctx);
      case 'backendKB':   return this._fetchBackendKB(text, tourData, signal, ctx);
      default:
        console.warn(`🤖 AgenticRAG: unknown source "${name}"`);
        return Promise.resolve(null);
    }
  }

  // ── Fetchers — throw on failure (so timing can tell error from empty) ──

  async _fetchIndexSearch(text, tourData, signal) {
    const links = tourData?.ragLinks || [];
    if (!links.length) return null;
    const pick = Array.isArray(links) ? links[0] : links;
    const origin = typeof pick === 'object' ? (pick.origin || null) : null;
    const index = typeof pick === 'object' ? (pick.index || pick.url || null) : (pick || null);
    if (!index) return null;
    const idxData = await APIController.searchIndexParallel({ origin, index, text, signal });
    const contextText = normalizeContext((idxData && idxData.context) || '');
    if (!contextText) return null;
    console.log(`🤖 AgenticRAG: indexSearch → ${contextText.length} chars`);
    return { source: 'indexSearch', text: contextText };
  }

  async _fetchRoomsData(text, tourData, signal, ctx) {
    const resp = await APIController.searchRoomParallel({
      text, roomsData: tourData.roomsData, currentRoom: ctx?.roomName || '', signal,
    });
    const raw = resp && resp.context;
    const ctxRooms = !raw ? '' : (typeof raw === 'string' ? raw.trim() : JSON.stringify(raw));
    if (!ctxRooms) return null;
    console.log(`🤖 AgenticRAG: roomsData → ${ctxRooms.length} chars`);
    return { source: 'roomsData', text: ctxRooms };
  }

  async _fetchBackendKB(text, tourData, signal, ctx) {
    const { roomName, tourType } = ctx || {};
    const steering = [
      tourType === 'MUSEUM' ? 'This is a museum tour.' : tourType ? 'This is a property tour.' : '',
      roomName ? `The visitor is currently in: ${roomName}. Prefer passages about it when the question is vague.` : '',
    ].filter(Boolean).join(' ');
    const results = await APIController.searchKnowledgeBaseParallel(text, { kbSelect: tourData.kbSelect, steering }, 3, signal);
    if (!results.length) return null;
    // KB rows can be whole web pages (~6k chars) and the same row can come back
    // twice; injected raw, the first row alone filled MAX_CONTEXT_CHARS and the
    // answering passage was cut. Dedupe, then keep each row's best passage.
    // Same page can be stored twice under different ids — dedupe on id AND text.
    const seen = new Set();
    const rows = results.filter(r => {
      const keys = [r.id != null ? `id:${r.id}` : null, `t:${(r.text || '').slice(0, 300)}`].filter(Boolean);
      if (keys.some(k => seen.has(k))) return false;
      keys.forEach(k => seen.add(k));
      return true;
    });
    const perRow = Math.floor(MAX_CONTEXT_CHARS / rows.length) - 20;
    const contextText = rows
      .map(r => `[${r.title || r.id || 'KB'}] ${bestPassage(r.text || '', text, perRow)}`)
      .join('\n');
    console.log(`🤖 AgenticRAG: backendKB → ${contextText.length} chars`);
    return { source: 'backendKB', text: contextText };
  }
}

export default AgenticRAGManager;
